/**
 * Ergonomic entry point for calculator form (pre-comparator) analytics.
 *
 * Owns the per-session state that ties the funnel together:
 *   - `calculatorSessionId` — a client-side UUID minted when the form starts. It
 *     is the join key across form steps BEFORE a `simulationId` exists (the
 *     latter is server-issued and only appears once the demand is persisted).
 *   - step view/completion sets + max step reached — feed the funnel and the
 *     completeness metrics on `calculator_offers_requested`.
 *   - an initial deep-clone of the demand — lets field events report `changed`
 *     (engaged) rather than merely `filled` (almost everything is pre-filled).
 *
 * `calculator_offers_requested` carries BOTH `calculatorSessionId` and
 * `simulationId`, so it stitches the form funnel to the `comparator_v25_*`
 * events (which carry simulationId) for cross-surface analysis.
 *
 * Only one simulation form is on screen at a time, so session state is a module
 * singleton, reset on each `markStarted()`. PII filtering happens in
 * `calculatorAnalytics.ts`; every method is fire-and-forget.
 */
import { useSimulationStore } from '@/components/calculator/services/SimulationStore';
import { CalculatorEvent, captureCalculatorEvent } from '@/plugins/calculatorAnalytics';
import { FormSteps } from '@/components/calculator/FormSteps';
import { getFormFieldSummary, getStepFieldStats } from '@/components/calculator/services/calculatorFormFields';
import { genUUID } from '@/services/utils/BasicUtils';
import EnvironmentService from '@/env/EnvironmentService';
import { cloneDeep } from 'lodash-es';

let sessionId: Nullable<string> = null;
let initialInput: any = null;
const stepsViewed = new Set<number>();
const stepsCompleted = new Set<number>();
const validationErrorSteps = new Set<number>();
let maxStepReached = 0;
let stepEnteredAt = 0;

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : 0);

export const useCalculatorAnalytics = () => {
  const store = useSimulationStore();

  const tenant = (): string =>
    EnvironmentService.Environment.isAppDomain().LENDI_PL ? 'LENDI_PL' : 'NESTO_RO';

  const stepName = (step: number): string => FormSteps[step] ?? String(step);

  const baseProperties = (): Record<string, unknown> => ({
    calculatorSessionId: sessionId,
    simulationId: store.simulationId ?? null,
    productType: store.productType ? String(store.productType) : null,
    tenant: tenant(),
    loanPurpose: store.userInput?.loanPurpose?.activity ?? null,
  });

  const track = (event: CalculatorEvent, properties: Record<string, unknown> = {}): void => {
    captureCalculatorEvent(event, { ...baseProperties(), ...properties, });
  };

  const resetSession = (): void => {
    sessionId = genUUID();
    initialInput = store.userInput ? cloneDeep(store.userInput) : null;
    stepsViewed.clear();
    stepsCompleted.clear();
    validationErrorSteps.clear();
    maxStepReached = 0;
    stepEnteredAt = now();
  };

  const emitStepFields = (step: number): void => {
    const { fields, } = getStepFieldStats(step, store, initialInput);
    fields.forEach((field) => {
      if (!field.visible) {
        return;
      }
      track(CalculatorEvent.FIELD_COMPLETED, {
        stepNumber: step,
        stepName: stepName(step),
        fieldName: field.name,
        filled: field.filled,
        changed: field.changed,
        optional: field.optional,
        conditional: field.conditional,
      });
    });
  };

  const markStarted = (): void => {
    resetSession();
    track(CalculatorEvent.STARTED, {
      source: store.deal ? 'deal' : 'fresh',
      householdsCount: store.userInput?.households?.length ?? 0,
    });
  };

  const markStepViewed = (step: number): void => {
    if (!sessionId) {
      resetSession();
    }
    stepEnteredAt = now();
    if (step > maxStepReached) {
      maxStepReached = step;
    }
    const firstView = !stepsViewed.has(step);
    stepsViewed.add(step);
    if (firstView) {
      track(CalculatorEvent.STEP_VIEWED, { stepNumber: step, stepName: stepName(step), });
    }
  };

  const markStepCompleted = (step: number): void => {
    if (stepsCompleted.has(step)) {
      return;
    }
    stepsCompleted.add(step);
    track(CalculatorEvent.STEP_COMPLETED, {
      stepNumber: step,
      stepName: stepName(step),
      durationMs: Math.round(now() - stepEnteredAt),
      householdsCount: store.userInput?.households?.length ?? 0,
      borrowersCount: store.userInput?.households?.[0]?.borrowers?.length ?? 0,
    });
    emitStepFields(step);
  };

  const markStepBack = (fromStep: number, toStep: number): void => {
    track(CalculatorEvent.STEP_BACK, { fromStep, toStep, });
  };

  const markValidationError = (step: number): void => {
    // Once per step per session — the "Next" button is disabled while invalid,
    // so this signals "the advisor got stuck on this step", not every keystroke.
    if (validationErrorSteps.has(step)) {
      return;
    }
    validationErrorSteps.add(step);
    track(CalculatorEvent.VALIDATION_ERROR, { stepNumber: step, stepName: stepName(step), });
  };

  const markOffersRequested = (fromStep: number): void => {
    const summary = getFormFieldSummary(store, initialInput);
    // Capture field-level stats for the step the advisor leaves from, even when
    // they jump straight to offers without pressing "Next".
    emitStepFields(fromStep);
    track(CalculatorEvent.OFFERS_REQUESTED, {
      fromStep,
      maxStepReached,
      stepsViewedCount: stepsViewed.size,
      stepsCompletedCount: stepsCompleted.size,
      visibleFieldsCount: summary.visibleCount,
      engagedFieldsCount: summary.engagedCount,
      householdsCount: store.userInput?.households?.length ?? 0,
      borrowersCount: store.userInput?.households?.[0]?.borrowers?.length ?? 0,
      hasAdditionalIncome: !!store.userInput?.households?.[0]?.borrowers?.[0]?.hasAdditionalIncomeSource,
      hasLiabilities: !!store.userInput?.households?.[0]?.hasLiabilities,
    });
  };

  const markOffersRequestFailed = (fromStep: number, errorCode: Nullable<string> = null): void => {
    track(CalculatorEvent.OFFERS_REQUEST_FAILED, { fromStep, errorCode: errorCode ?? null, });
  };

  return {
    track,
    markStarted,
    markStepViewed,
    markStepCompleted,
    markStepBack,
    markValidationError,
    markOffersRequested,
    markOffersRequestFailed,
    CalculatorEvent,
  };
};
